import React from "react";
import { styled } from "styled-components";

import type { DateRangeField } from "../types";
import { FieldGroup, HelperText, Label } from "../shared";
import { Icon } from "../../Icon";
import { useTheme } from "../../../theme";

const CalendarContainer = styled.div(({ theme }) => ({
  marginTop: theme.space[5],
  display: "flex",
  flexDirection: "column",
  gap: theme.space[3],
}));

const CalendarHeader = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const Nav = styled.button(({ theme }) => ({
  border: `1px solid ${theme.colors.grayMed}`,
  borderRadius: theme.radii["1"],
  background: theme.colors.white,
  color: theme.colors.black,
  height: theme.sizes.sm,
  padding: `0 ${theme.space["3"]}`,
  cursor: "pointer",
}));

const MonthTitle = styled.div(() => ({ fontWeight: 600 }));

const Grid = styled.div(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: 4,
}));

const DayCell = styled.button<{
  $isToday?: boolean;
  $isSelected?: boolean;
  $inRange?: boolean;
  $disabled?: boolean;
}>(({ theme, $isToday, $isSelected, $inRange, $disabled }) => ({
  height: 36,
  borderRadius: theme.radii["1"],
  border: `1px solid ${$isSelected ? theme.colors.primary : "transparent"}`,
  backgroundColor: $isSelected
    ? theme.colors.primaryLight
    : $inRange
      ? theme.colors.primaryLight
      : theme.colors.white,
  color: $disabled ? theme.colors.grayDark : theme.colors.black,
  opacity: $disabled ? 0.5 : 1,
  cursor: $disabled ? "not-allowed" : "pointer",
  textDecoration: $isToday ? "underline" : "none",
  fontWeight: $isToday ? 600 : 400,
}));

const Weekday = styled.div(({ theme }) => ({
  fontSize: theme.fontSizes["1"],
  color: theme.colors.grayDark,
  textAlign: "center",
}));

function startOfDay(d: Date): Date {
  const nd = new Date(d);
  nd.setHours(0, 0, 0, 0);
  return nd;
}

function parseISO(dateStr?: string): Date | null {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return startOfDay(new Date(y, mo - 1, d));
}

function formatISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addMonths(d: Date, months: number): Date {
  const nd = new Date(d);
  nd.setMonth(nd.getMonth() + months);
  return nd;
}

function getMonthMatrix(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const matrix: (Date | null)[] = [];
  // Weekday index: 0=Sunday
  const startOffset = first.getDay();
  for (let i = 0; i < startOffset; i++) matrix.push(null);
  for (let d = 1; d <= last.getDate(); d++)
    matrix.push(new Date(year, month, d));
  return matrix;
}

function clampToDay(d: Date, min?: Date | null, max?: Date | null): boolean {
  const t = d.getTime();
  if (min && t < min.getTime()) return false;
  if (max && t > max.getTime()) return false;
  return true;
}

export const DateRangePicker: React.FC<{
  field: DateRangeField;
  value?: { start?: string; end?: string };
  onChange: (val: { start: string; end?: string }) => void;
}> = ({ field, value, onChange }) => {
  const today = startOfDay(new Date());
  const baseMin = parseISO(field.minDate || undefined);
  const max = parseISO(field.maxDate || undefined);
  // If disablePastDates is true, clamp the minimum selectable day to today
  const min = field.disablePastDates
    ? baseMin && baseMin.getTime() > today.getTime()
      ? baseMin
      : today
    : baseMin;

  const theme = useTheme();
  const [hasInitialized, setHasInitialized] = React.useState(false);

  // Determine the default date within valid range
  const getDefaultDate = (): Date => {
    const todayTime = today.getTime();
    const isInRange =
      (!min || todayTime >= min.getTime()) &&
      (!max || todayTime <= max.getTime());

    if (isInRange) return today;
    if (min) return min;
    if (max) return max;
    return today;
  };

  // Check if field has a default value
  const hasDefaultValue = field.defaultValue?.start;

  // Initialize with default value or auto-select date on mount
  React.useEffect(() => {
    if (hasInitialized) return;

    // If value is already set (from parent or defaultValue), use it
    if (value?.start) {
      setHasInitialized(true);
      return;
    }

    // If field has defaultValue, use it
    if (hasDefaultValue) {
      onChange(field.defaultValue);
      setHasInitialized(true);
      return;
    }

    // Otherwise, auto-select default date
    const defaultDate = getDefaultDate();
    const iso = formatISO(defaultDate);
    onChange({ start: iso, end: iso });
    setHasInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialized]);

  const startDate = parseISO(value?.start || undefined);
  const endDate = parseISO(value?.end || undefined);

  const [visibleMonth, setVisibleMonth] = React.useState<Date>(
    startDate || getDefaultDate(),
  );

  const matrix = getMonthMatrix(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
  );

  const maxSpanDays = field.maxSpanDays || undefined;

  function isInRange(d: Date): boolean {
    if (!startDate || !endDate) return false;
    const t = d.getTime();
    return t >= startDate.getTime() && t <= endDate.getTime();
  }

  function canSelect(d: Date): boolean {
    if (!clampToDay(d, min, max)) return false;
    if (startDate && !endDate && maxSpanDays) {
      const diff = Math.abs(
        Math.floor(
          (startOfDay(d).getTime() - startDate.getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      );
      return diff <= maxSpanDays - 1;
    }
    return true;
  }

  function handleDayClick(d: Date) {
    if (!canSelect(d)) return;
    const clickedDate = startOfDay(d);

    // First click or starting over: set start date only
    if (!startDate || (startDate && endDate)) {
      onChange({ start: formatISO(clickedDate), end: undefined });
    }
    // Second click: complete the range or start over
    else if (startDate && !endDate) {
      if (clickedDate.getTime() < startDate.getTime()) {
        // Clicked before start, start over with new selection
        onChange({ start: formatISO(clickedDate), end: undefined });
      } else if (clickedDate.getTime() === startDate.getTime()) {
        // Clicked same date, set as single date range
        onChange({ start: formatISO(startDate), end: formatISO(startDate) });
      } else {
        // Clicked after start, complete the range
        onChange({ start: formatISO(startDate), end: formatISO(clickedDate) });
      }
    }
  }

  const monthLabel = visibleMonth.toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });

  const weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  return (
    <FieldGroup key={field.id}>
      {field.label && (
        <Label>
          {field.label}
          {field.required && (
            <span style={{ color: theme.colors.warn }}> *</span>
          )}
        </Label>
      )}
      {field.helpText && <HelperText>{field.helpText}</HelperText>}
      <CalendarContainer>
        <CalendarHeader>
          <Nav onClick={() => setVisibleMonth((d) => addMonths(d, -1))}>
            <Icon
              name="large-arrow-left"
              size={16}
              color={theme.colors.black}
            />
          </Nav>
          <MonthTitle>{monthLabel}</MonthTitle>
          <Nav onClick={() => setVisibleMonth((d) => addMonths(d, 1))}>
            <Icon
              name="large-arrow-right"
              size={16}
              color={theme.colors.black}
            />
          </Nav>
        </CalendarHeader>
        <Grid>
          {weekdays.map((w) => (
            <Weekday key={w}>{w}</Weekday>
          ))}
          {matrix.map((d, idx) => {
            if (!d) return <div key={`empty-${idx}`} style={{ height: 36 }} />;
            const isToday = d.getTime() === today.getTime();
            const isSelected =
              (startDate && d.getTime() === startDate.getTime()) ||
              (endDate && d.getTime() === endDate.getTime());
            const disabled = !canSelect(d);
            const inRange = !!(startDate && endDate && isInRange(d));
            return (
              <DayCell
                key={d.toISOString()}
                $isToday={Boolean(isToday)}
                $isSelected={Boolean(isSelected)}
                $inRange={Boolean(inRange)}
                $disabled={Boolean(disabled)}
                onClick={() => handleDayClick(d)}
              >
                {d.getDate()}
              </DayCell>
            );
          })}
        </Grid>
      </CalendarContainer>
      {startDate && (
        <HelperText style={{ marginTop: theme.space[4] }}>
          Selected: {startDate.toLocaleDateString()}
          {endDate && endDate.getTime() !== startDate.getTime() && (
            <> - {endDate.toLocaleDateString()}</>
          )}
        </HelperText>
      )}
    </FieldGroup>
  );
};
